import React, { useEffect, useState, useRef } from "react";
import Peer from "peerjs";
import { customAlphabet } from "nanoid";
import Webcam from "react-webcam";
import DropFont from "./images/dropfont.webp";
import QuestionImg from "./images/question.webp";
import DropIntroImg from "./images/drop_intro_image_full.webp";
import DropIntroImgNullText from "./images/drop_intro_image_full_null_text.webp";
import EvilEmoji from "./images/evil_emoji.webp";
import ConsentImg from "./images/consent.webp";
import "./KioskPage.css";

const Typewriter = ({ text, speed = 60 }) => {
  const [displayedText, setDisplayedText] = useState("");

  useEffect(() => {
    setDisplayedText("");
    let index = 0;
    const typeNextChar = () => {
      if (index < text.length) {
        setDisplayedText(text.slice(0, index + 1));
        const currentChar = text[index];
        index++;
        const delay =
          currentChar === "." || text[index] === "." ? speed * 3 : speed;
        setTimeout(typeNextChar, delay);
      }
    };
    typeNextChar();
  }, [text, speed]);

  return <span>{displayedText}</span>;
};

const KioskPage = () => {
  const [kioskId, setKioskId] = useState(null);
  const [conn, setConn] = useState(null);
  const [answerSent, setAnswerSent] = useState(false);
  const [sequenceStarted, setSequenceStarted] = useState(false);
  const [displayMessage, setDisplayMessage] = useState("");
  const [secondLineDisplayMessage, setSecondLineDisplayMessage] = useState("");
  const [topDisplayMessage, setTopDisplayMessage] = useState("");
  const [showImage, setShowImage] = useState(false);
  const [showSequenceWebcam, setShowSequenceWebcam] = useState(false);
  const [showQuestion, setShowQuestion] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [flashActive, setFlashActive] = useState(false);
  const [showCameraIcon, setShowCameraIcon] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [blackScreen, setBlackScreen] = useState(false);

  const webcamRef = useRef(null);
  const peerRef = useRef(null);
  const [localStream, setLocalStream] = useState(null);
  const [callInitiated, setCallInitiated] = useState(false);

  useEffect(() => {
    const nanoid = customAlphabet(
      "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz",
      6
    );
    const shortId = nanoid();
    const peer = new Peer(shortId);
    peerRef.current = peer;
    peer.on("open", (id) => {
      console.log("Kiosk Peer ID:", id);
      console.log(
        "Phone URL:",
        `${window.location.origin}/phone?kioskId=${id}`
      );

      peer.on("connection", (incomingConn) => {
        console.log("Received connection from phone:", incomingConn);
      
        // Wait until this new connection is actually open:
        incomingConn.on("open", () => {
          console.log("Data connection is now open:", incomingConn.peer);
          setConn(incomingConn); 
        });
      });
    
      setKioskId(id);
      const encodedId = id;
      // Send the encodedId to the server to save it
      fetch("http://localhost:3000/id", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ encodedId }),
      })
        .then((response) => response.json())
        .then((data) => console.log("ID saved successfully", data))
        .catch((error) => console.error("Error saving ID:", error));
    });

    return () => {
      peer.destroy();
    };
  }, []);

  // useeffect for if no connection is made after 60 seconds, refresh
  useEffect(() => {
    if (!conn) {
      const timer = setTimeout(() => {
        console.log("No connection made after 4 mins. Refreshing page.");
        window.location.reload();
      }, 240000);
      return () => clearTimeout(timer);
    }
  }, [conn]);

  // Get local webcam stream.
  useEffect(() => {
    navigator.mediaDevices
      .getUserMedia({ video: true, audio: false })
      .then((stream) => {
        // lower resolution for performance
        const videoTrack = stream.getVideoTracks()[0];
        const constraints = {
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 30 },
        };
        videoTrack
          .applyConstraints(constraints)
          .then(() => {
            // set local stream to the modified one
            setLocalStream(stream);
          })
          .catch((err) => {
            console.error("Failed to apply constraints:", err);
            setLocalStream(stream);
          });
      })
      .catch((err) => {
        console.error("Error accessing webcam for stream:", err);
      });
  }, []);

  // Initiate call once connection and local stream are available.
  useEffect(() => {
    if (conn && localStream && !callInitiated) {
      console.log("Initiating call to phone with id:", conn.peer);
      const call = peerRef.current.call(conn.peer, localStream);
      call.on("error", (err) => {
        console.error("Call error:", err);
      });
      setCallInitiated(true);
    }
  }, [conn, localStream, callInitiated]);

  // Start selfie countdown only after consent is given.
  useEffect(() => {
    if (conn && consentGiven && !showQuestion) {
      setCountdown(5);
      const interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            // Capture the selfie and store/send it.
            if (webcamRef.current) {
              const imageSrc = webcamRef.current.getScreenshot();
              if (imageSrc) {
                localStorage.setItem("kioskPhoto", imageSrc);
                console.log("Photo captured and stored in localStorage");
                if (conn && conn.open) {
                  conn.send({ selfie: imageSrc });
                  console.log("Selfie sent to phone via PeerJS");
                }
              } else {
                console.log("Failed to capture photo");
              }
            }
            // Show camera icon, flash and then trigger question phase.
            setShowCameraIcon(true);
            setFlashActive(true);
            setTimeout(() => {
              setFlashActive(false);
            }, 300);
            setTimeout(() => {
              setShowQuestion(true);
            }, 1000);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [conn, consentGiven, showQuestion]);

  // Refresh page if no answer is given within 20 seconds for consent.
  useEffect(() => {
    if (conn && !sequenceStarted && !answerSent && !consentGiven) {
      setShowSequenceWebcam(true);
      const timer = setTimeout(() => {
        console.log("No answer given within 20 seconds. Refreshing page.");
        window.location.reload();
      }, 20000);
      return () => clearTimeout(timer);
    }
  }, [conn, answerSent, sequenceStarted, consentGiven]);

  // Refresh page if no answer is given within 20 seconds for question.
  useEffect(() => {
    if (conn && !sequenceStarted && !answerSent && consentGiven) {
      setShowSequenceWebcam(true);
      const timer = setTimeout(() => {
        console.log("No answer given within 20 seconds. Refreshing page.");
        window.location.reload();
      }, 20000);
      return () => clearTimeout(timer);
    }
  }, [conn, answerSent, sequenceStarted, consentGiven]);

  const startSequence = () => {
    setSequenceStarted(true);
    setShowSequenceWebcam(true);
    setTopDisplayMessage("😈");
    setDisplayMessage("Don't trust every drop you get...");
    setTimeout(() => {
      setSecondLineDisplayMessage("‎ Look down at your phone");
    }, 3500);
    setTimeout(() => {
      setBlackScreen(true);
    }, 8100);

    setTimeout(() => {
      setSecondLineDisplayMessage("");
      setTopDisplayMessage("");
      setDisplayMessage("I'm always watching");
      setTimeout(() => {
        setSecondLineDisplayMessage("Check your phone");
      }, 6000);
      setTimeout(() => {
        setShowSequenceWebcam(false);
        setShowImage(true);
        setTimeout(() => {
          window.location.reload();
        }, 5000);
      }, 15000);
    }, 20000);
  };

  const handleAnswer = (answer) => {
    //if (conn && conn.open) {
    const message = answer === "yes" ? "ready" : "not_ready";
    conn.send({ status: message, kioskId });
    setAnswerSent(true);
    setBlackScreen(true);
    startSequence();
    // }
  };

  // Handle phone disconnection.
  useEffect(() => {
    if (conn) {
      conn.on("error", (err) => {
        console.error("Connection error:", err);
        //  window.location.reload();
      });
    }
  }, [conn]);

  return (
    <>
      <button
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "20%",
          height: "20%",
          backgroundColor: "blue",
          opacity: 0,
          zIndex: 9999,
        }}
        onClick={
          () => window.location.reload() // Close the kiosk window if pressed (debug button)
        }
      ></button>
      <div style={{ textAlign: "center" }}>
        {/* Consent Screen: Shown until the user gives consent */}
        {conn && !consentGiven && !sequenceStarted && (
          <div>
            <div style={{ position: "relative" }}>
              <img
                src={ConsentImg}
                alt="Consent"
                style={{ width: "85%", height: "85%", objectFit: "cover" }}
              />
              <button
                style={{
                  backgroundColor: "#2BDB2E",
                  borderRadius: ".35rem",
                  border: "1rem",
                  color: "white",
                  marginLeft: "1rem",
                  position: "absolute",
                  top: "50%",
                  left: "51%",
                  transform: "translate(-155%, 80%)",
                  textShadow: "0 0 10px rgba(0,0,0,0.7)",
                  fontSize: "7rem",
                  padding: "1rem",
                  width: "16rem",
                  height: "12rem",
                }}
                onClick={() => setConsentGiven(true)}
              >
                Yes
              </button>
              <button
                style={{
                  backgroundColor: "#E72929",
                  borderRadius: ".35rem",
                  border: "1rem",
                  color: "white",
                  marginRight: "1rem",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(35%, 80%)",
                  textShadow: "0 0 10px rgba(0,0,0,0.7)",
                  fontSize: "7rem",
                  padding: "1rem",
                  width: "16rem",
                  height: "12rem",
                }}
                onClick={() => {
                  window.location.href = window.location.origin + "/kiosk";
                  window.location.reload();
                }
                }
              >
                No
              </button>
            </div>
          </div>
        )}

        {/* Selfie countdown and webcam display (after consent is given) */}
        {!showQuestion && consentGiven && (
          <div>
            <h2
              style={{
                color: "white",
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -190%)", // increased size and moved up by 1rem
                textShadow: "0 0 10px rgba(0,0,0,0.7)",
                fontSize: "8rem", // increased size
                padding: "1rem",
              }}
            >
              {showCameraIcon ? "" : "Smile!"}
              {flashActive ? "📸" : ""}
            </h2>
            <h2
              style={{
                color: "white",
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -70%)", // moved up by 1rem
                textShadow: "0 0 10px rgba(0,0,0,0.7)",
                fontSize: "8rem", // increased size
                padding: "1rem",
              }}
            >
              {showCameraIcon ? "" : `${countdown}`}
            </h2>
            {flashActive && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  backgroundColor: "white",
                  opacity: 0.8,
                  zIndex: 9999,
                  pointerEvents: "none",
                }}
              ></div>
            )}
          </div>
        )}

        {/* Display captured photo once the camera icon is shown */}
        {showCameraIcon && !showQuestion && consentGiven && (
          <img
            src={localStorage.getItem("kioskPhoto")}
            alt="Captured"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}

        {/* Question Phase: Shown after the selfie countdown */}
        {conn && showQuestion && !sequenceStarted && (
          <div>
            <div style={{ position: "relative" }}>
              <img
                src={localStorage.getItem("kioskPhoto")}
                alt="Captured"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              <img
                src={QuestionImg}
                alt="Question"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -65%)",
                  width: "60%",
                  marginBottom: "6rem",
                }}
              />
            </div>
            {!answerSent ? (
              <>
                <button
                  style={{
                    backgroundColor: "#2BDB2E",
                    borderRadius: ".35rem",
                    border: "1rem",
                    color: "white",
                    marginLeft: "1rem",
                    position: "absolute",
                    top: "50%",
                    left: "51%",
                    transform: "translate(-155%, 210%)",
                    textShadow: "0 0 10px rgba(0,0,0,0.7)",
                    fontSize: "6rem",
                    padding: "1rem",
                    width: "14rem",
                    height: "10rem",
                  }}
                  onClick={() => handleAnswer("yes")}
                >
                  Yes
                </button>
                <button
                  style={{
                    backgroundColor: "#E72929",
                    borderRadius: ".35rem",
                    border: "1rem",
                    color: "white",
                    marginRight: "1rem",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(45%, 210%)",
                    textShadow: "0 0 10px rgba(0,0,0,0.7)",
                    fontSize: "6rem",
                    padding: "1rem",
                    width: "14rem",
                    height: "10rem",
                  }}
                  onClick={() => handleAnswer("yes")}
                >
                  No
                </button>
              </>
            ) : (
              <p>Answer sent. Starting sequence...</p>
            )}
          </div>
        )}

        {/* Other visual elements (e.g., NFC prompt, animated dropfont) */}
        {useEffect(() => {
          if (!conn) {
            const interval = setInterval(
              () => {
                setShowImage((prev) => !prev);
              },
              showImage ? 3000 : 1000
            );
            return () => clearInterval(interval);
          }
        }, [conn, showImage])}

        {showSequenceWebcam ? (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              zIndex: -1,
              overflow: "hidden",
            }}
          >
            {consentGiven && displayMessage !== "I'm always watching" ? (
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              // cover screen black
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "black",
                }}
              ></div>
            )}
            {blackScreen && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "black",
                }}
              ></div>
            )}
            <div>
              <h2
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -180%)",
                  textAlign: "center",
                  color: "white",
                  textShadow: "0 0 10px rgba(0,0,0,0.7)",
                  fontSize: "5rem",
                  padding: "1rem",
                }}
              >
                <div
                  style={{
                    width: "20%",
                    height: "20%",
                    textAlign: "left",
                    marginBottom: "1rem",
                  }}
                >
                  {topDisplayMessage === "😈" && (
                    <img
                      src={EvilEmoji}
                      alt="emoji"
                      style={{ display: "none", transform: "scale(1.8)" }}
                    />
                  )}
                </div>
              </h2>
            </div>
            {displayMessage === "I'm always watching" && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "black",
                  zIndex: -1,
                }}
              ></div>
            )}
            <div className="typewriter">
              <h2
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform:
                    displayMessage === "I'm always watching"
                      ? "translate(-29%, -120%)"
                      : "translate(-44%, -120%)",
                  width: "70%",
                  textAlign: "center",
                  color: "white",
                  textShadow: "0 0 10px rgba(0,0,0,0.7)",
                  fontSize: "5rem",
                  padding: "1rem",
                }}
              >
                <div style={{ width: "100%", textAlign: "left" }}>
                  <Typewriter text={displayMessage} />
                </div>
              </h2>
              <h2
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform:
                    secondLineDisplayMessage === "Check your phone"
                      ? "translate(-26%, -40%)"
                      : "translate(-40%, -40%)",
                  width: "70%",
                  textAlign: "center",
                  color: "white",
                  textShadow: "0 0 10px rgba(0,0,0,0.7)",
                  fontSize: "5rem",
                  padding: "1rem",
                }}
              >
                <div style={{ width: "100%", textAlign: "left" }}>
                  <Typewriter text={secondLineDisplayMessage} />
                </div>
              </h2>
            </div>
            {topDisplayMessage === "😈" &&
              setTimeout(() => {
                document.querySelector(
                  "img[src='" + EvilEmoji + "']"
                ).style.display = "block";
              }, 1000)}
          </div>
        ) : (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              margin: 0,
              padding: 0,
              overflow: "hidden",
            }}
          >
            {!conn && (
              <div>
                <img
                  src={showImage ? DropIntroImg : DropIntroImgNullText}
                  alt="DropIntro"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    pointerEvents: "none",
                  }}
                />
              </div>
            )}
            {conn && (
              <div className="animated-container">
                <img
                  src={DropFont}
                  alt="DropMovieADPicture"
                  className="dropfont-animation"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default KioskPage;
